import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  Backdrop,
} from "@mui/material";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import SehoLoadingSpinner from "../../../GlobalFunction/SehoLoadingSpinner";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getLangFromCookie } from "../../../../Utils/LangCookie";

/* ----------------------------- i18n texts ----------------------------- */
const fonts = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed jp",
};

const TEXT = {
  breadcrumbHome: {
    ZH: "首頁",
    EN: "Home",
    JP: "ホーム",
  },
  pageTitle: {
    ZH: "合作資訊",
    EN: "Partnership Information",
    JP: "協業に関するご案内",
  },
  tileRecruit: {
    ZH: "招募訊息",
    EN: "Recruitment Opportunities",
    JP: "採用情報",
  },
  tilePartner: {
    ZH: "技術合作及聯合開發諮詢",
    EN: "Technical Collaboration & Development",
    JP: "技術協業・共同開発に関するご相談",
  },
};

const COMPANY_INFORMATION_IMAGE_SOURCES = [
  "/img/companyInfo/recruit.webp",
  "/img/companyInfo/partner.webp",
];

// -------------------- Tile --------------------
function InfoTile({
  img,
  title,
  to,
  accent,
  side = "left",
  lang,
  imageFit = "cover",
  imageHoverTransform = "scale(1.03)",
}) {
  const isExternal = /^https?:\/\//i.test(String(to || ""));

  return (
    <Stack
      spacing={{ xs: 0.05, md: 1.25 }}
      alignItems="center"
      sx={{ width: "100%" }}
    >
      <Box
        component={isExternal ? "a" : RouterLink}
        href={isExternal ? to : undefined}
        to={!isExternal ? to : undefined}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        sx={{
          textDecoration: "none",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: { xs: "100%", sm: 560, md: 620 },
          minWidth: 0,

          "& .tile-card": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            aspectRatio: {
              xs: "16 / 11",
              sm: "16 / 10",
              md: "16 / 10",
            },
            height: "auto",
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#fff",
            boxShadow: "0 6px 18px rgba(0,0,0,.08)",
            transform: "translateZ(0)",
            transition:
              "transform 260ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 260ms ease",
          },

          "& .tile-img": {
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: imageFit,
            objectPosition: "center",
            backgroundColor: "#fff",
            transition: "transform 260ms cubic-bezier(0.34,1.56,0.64,1)",
          },

          "& .tile-caption": {
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            mt: { xs: 0.35, md: 2 },
            fontSize: { xs: 17, md: 20 },
            fontWeight: 700,
            fontFamily: fonts[lang],
            color: accent,
          },

          "& .tile-text": {
            transition: "transform .25s cubic-bezier(0.34,1.56,0.64,1)",
          },

          "& .tile-arrowWrap": {
            transition:
              "transform .25s cubic-bezier(0.34,1.56,0.64,1), background-color .25s ease",
            width: 24,
            height: 24,
            borderRadius: "50%",
            bgcolor: alpha(accent, 0.12),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          },

          "& .tile-caption::after": {
            content: '""',
            position: "absolute",
            left: 0,
            bottom: -2,
            height: 2,
            width: 0,
            backgroundColor: accent,
            transition: "width .25s ease",
          },

          "&:hover .tile-card": {
            transform: `translateY(-6px) rotate(${side === "left" ? "-1.1deg" : "1.1deg"})`,
            boxShadow: "0 18px 42px rgba(0,0,0,.14)",
          },

          "&:hover .tile-img": {
            transform: imageHoverTransform,
          },

          "&:hover .tile-text": {
            transform: "translateY(-1px)",
          },

          "&:hover .tile-arrowWrap": {
            transform: "translateX(4px) scale(1.05)",
            bgcolor: alpha(accent, 0.18),
          },

          "&:hover .tile-caption::after": {
            width: "100%",
          },
        }}
      >
        <Paper elevation={3} className="tile-card">
          <Box
            component="img"
            className="tile-img"
            src={img}
            alt={title}
            loading="eager"
            fetchPriority="high"
            decoding="sync"
          />
        </Paper>

        <Box className="tile-caption">
          <Box component="span" className="tile-text">
            {title}
          </Box>
          <Box className="tile-arrowWrap">
            <ArrowForwardIosRoundedIcon sx={{ fontSize: 16, color: accent }} />
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}

// -------------------- Page --------------------
export default function CompanyInformation({ mode = "b2b" }) {
  const accent = getPrimaryColor(mode);

  const [lang, setLang] = useState("ZH");

  const [areTileImagesReady, setAreTileImagesReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const preloadImage = (src) =>
      new Promise((resolve) => {
        const image = new Image();

        image.onload = () => resolve(true);

        /*
         * Resolve on error too so the page never gets stuck behind
         * the loading screen forever if one image fails on the server.
         */
        image.onerror = () => resolve(false);

        image.src = src;

        if (image.complete) {
          resolve(true);
        }
      });

    Promise.all(
      COMPANY_INFORMATION_IMAGE_SOURCES.map((src) => preloadImage(src)),
    ).then(() => {
      if (isMounted) {
        setAreTileImagesReady(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const syncLang = () => {
      const c = getLangFromCookie();
      if (c === "EN" || c === "JP" || c === "ZH") setLang(c);
      else setLang("ZH");
    };

    if (typeof window !== "undefined") {
      syncLang();
      window.addEventListener("app:languageChanged", syncLang);
      return () => window.removeEventListener("app:languageChanged", syncLang);
    }
  }, []);

  const pageTitle = TEXT.pageTitle[lang] || TEXT.pageTitle.ZH;

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Backdrop
        open={!areTileImagesReady}
        transitionDuration={220}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 10,
          backgroundColor: "rgba(255, 255, 252, 0.92)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          pointerEvents: "auto",
          touchAction: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <SehoLoadingSpinner variant="overlay" />
        </Box>
      </Backdrop>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link underline="hover" color="inherit" href="/">
            {TEXT.breadcrumbHome[lang]}
          </Link>
          <Typography color="text.primary">{pageTitle}</Typography>
        </Breadcrumbs>
      </Container>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: 1200,
          px: { xs: 2, md: 3 },
          py: { xs: 6, md: 8 },
          mx: "auto",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, md: 3 },
            py: { xs: 6, md: 8 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr", md: "1fr 1fr" },
            columnGap: { md: 6 },
            rowGap: { xs: 4, md: 0 },
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              width: { xs: "auto", sm: "100%", md: "auto" },
              maxWidth: { xs: "none", sm: 560, md: "none" },
              justifySelf: { xs: "center", md: "start" },
            }}
          >
            <InfoTile
              img="/img/companyInfo/recruit.webp"
              title={TEXT.tileRecruit[lang] || TEXT.tileRecruit.ZH}
              to="https://www.104.com.tw/company/1a2x6bjo7z?jobsource=index_s"
              accent={accent}
              side="left"
              lang={lang}
            />
          </Box>

          <Box
            sx={{
              width: { xs: "auto", sm: "100%", md: "auto" },
              maxWidth: { xs: "none", sm: 560, md: "none" },
              justifySelf: { xs: "center", md: "end" },
            }}
          >
            <InfoTile
              img="/img/companyInfo/partner.webp"
              title={TEXT.tilePartner[lang] || TEXT.tilePartner.ZH}
              to="/合作資訊/技術合作及聯合開發諮詢"
              accent={accent}
              side="right"
              lang={lang}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
